/**
 * API client for Data Editor endpoints (outliers and notes management)
 */

import type {
  SensorDataPoint,
  OutlierResponse,
  OutlierUpdate,
  BulkOutlierUpdate,
  BulkOutlierResponse,
  NoteUpdateResponse,
  OutlierFilters,
} from '@/types/sensor';
import type { NoteUpdate } from '@/types/dataEditor';
import { buildApiUrl } from './config';
import { apiClient } from './client';
export const api = {
  // ==================== Parameter Management ====================

  /**
   * Fetch available parameters for a tank (parameters with actual data)
   * Now includes sources that have data for each parameter
   */
  async getTankParameters(tankId: string): Promise<{
    parameters: Array<{
      value: string;
      label: string;
      sources?: Array<{ value: string; label: string }>;
    }>;
  }> {
    const response = await apiClient(buildApiUrl(`/api/tanks/${tankId}/measurement-parameters`), {
      method: 'GET',
      headers: {},
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  // ==================== Outlier Management ====================

  /**
   * Fetch outliers with optional filtering and pagination
   */
  async getOutliers(tankId: string, filters?: OutlierFilters): Promise<OutlierResponse> {
    const url = new URL(buildApiUrl(`/api/tanks/${tankId}/outliers`), window.location.origin);

    // Add query parameters
    if (filters?.table_name) {
      url.searchParams.append('table_name', filters.table_name);
    }
    if (filters?.start_date) {
      url.searchParams.append('start_date', filters.start_date);
    }
    if (filters?.end_date) {
      url.searchParams.append('end_date', filters.end_date);
    }
    if (filters?.excluded !== undefined) {
      url.searchParams.append('excluded', String(filters.excluded));
    }
    if (filters?.page !== undefined) {
      url.searchParams.append('page', String(filters.page));
    }
    if (filters?.limit !== undefined) {
      url.searchParams.append('limit', String(filters.limit));
    }

    const response = await apiClient(url.toString(), {
      method: 'GET',
      headers: {
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Update single outlier exclusion status
   */
  async updateOutlier(tankId: string, update: OutlierUpdate): Promise<SensorDataPoint> {
    const response = await apiClient(buildApiUrl(`/api/tanks/${tankId}/outliers`), {
      method: 'PUT',
      headers: {
      },
      body: JSON.stringify(update),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Bulk update multiple outliers
   */
  async bulkUpdateOutliers(tankId: string, bulkUpdate: BulkOutlierUpdate): Promise<BulkOutlierResponse> {
    const response = await apiClient(buildApiUrl(`/api/tanks/${tankId}/outliers/bulk`), {
      method: 'PUT',
      headers: {
      },
      body: JSON.stringify(bulkUpdate),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  // ==================== Notes Management ====================

  /**
   * Update an existing note (inline editing)
   */
  async updateNote(tankId: string, update: NoteUpdate): Promise<NoteUpdateResponse> {
    const response = await apiClient(buildApiUrl(`/api/tanks/${tankId}/notes`), {
      method: 'PUT',
      body: JSON.stringify(update),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

};
